import { Dictionary, Point, PointDragDropObject, PointDragEventObject } from 'highcharts';
import { isNil, pathOr, uniq } from 'ramda';

import { Agent } from '../../Agents/types';
import { MIN_5 } from '../config';
import { ChartScheduleElement, NearestSurroundingDaysElements } from '../types';

/* eslint-disable no-restricted-syntax, no-continue */
export const handleElementDragDrop =
  (
    agents: Record<string, Agent>,
    visibleAgents: string[],
    startOfDay: number,
    endOfDay: number,
    nearestSurroundingDaysElements: NearestSurroundingDaysElements,
    productionScheduleElements: ChartScheduleElement[],
    nearestSurroundingDaysProductionElements: NearestSurroundingDaysElements
  ) =>
  (e: PointDragEventObject) => {
    const allPoints = e.target.series.points as Array<Point & ChartScheduleElement>;
    const draggedPoints = Object.values(
      e.newPoints as Dictionary<PointDragDropObject & { point: ChartScheduleElement }>
    );
    const draggedPointsKeys: string[] = draggedPoints.map((draggedPoint) => draggedPoint.point.key);
    const originPoints = pathOr({}, ['origin', 'points'], e);
    const yAxisDeltas: number[] = [];

    for (const { newValues, point: draggedPoint } of draggedPoints) {
      const y = typeof newValues.y !== 'undefined' ? newValues.y : draggedPoint.y;
      const start = typeof newValues.start !== 'undefined' ? newValues.start : draggedPoint.start;
      const end = typeof newValues.end !== 'undefined' ? newValues.end : draggedPoint.end;

      const targetAgentId = typeof newValues.y !== 'undefined' ? visibleAgents[newValues.y] : draggedPoint.agentId;
      const nearestPreviousDayElement = nearestSurroundingDaysElements.previousDay[targetAgentId];
      const nearestNextDayElement = nearestSurroundingDaysElements.nextDay[targetAgentId];
      const nearestPreviousDayProductionElement = nearestSurroundingDaysProductionElements.previousDay[targetAgentId];
      const nearestNextDayProductionElement = nearestSurroundingDaysProductionElements.nextDay[targetAgentId];

      const resizing = typeof newValues.start === 'undefined' || typeof newValues.end === 'undefined';
      const sizeDifference = (draggedPoint.end - draggedPoint.start) / (newValues.end - newValues.start);

      const isTargetAgentNotAssignedToQueuesUsedWithinSchedule =
        !isNil(y) && visibleAgents[y] && agents[visibleAgents[y]] ? agents[visibleAgents[y]].isNotAssigned : false;

      const isTargetAgentActive =
        !isNil(y) && visibleAgents[y] && agents[visibleAgents[y]] ? agents[visibleAgents[y]].active : true;
      const isChangeWithinSameAgent = originPoints[draggedPoint.id] ? y === originPoints[draggedPoint.id].y : true;

      const isInactiveOrNotAssignedAndChangeIsNotWithinSameAgent =
        (!isTargetAgentActive || isTargetAgentNotAssignedToQueuesUsedWithinSchedule) && !isChangeWithinSameAgent;

      /**
       * Disallow dropping schedule elements to an inactive agent or agent not assigned to any queues
       * used within schedule but allow to move elements within himself
       */

      if (isInactiveOrNotAssignedAndChangeIsNotWithinSameAgent) {
        return false;
      }

      // Forbid elements resize while dragging beyond chart boundaries
      if (!resizing && sizeDifference !== 1) {
        return false;
      }

      // Minimal element duration is 5 min
      if (end - start < MIN_5) {
        return false;
      }

      yAxisDeltas.push(draggedPoint.y! - newValues.y);

      // prevent from overlapping with nearest element from previous day
      if (start < startOfDay && nearestPreviousDayElement && nearestPreviousDayElement.end > start) {
        return false;
      }

      // prevent from overlapping with nearest element from next day
      if (end > endOfDay && nearestNextDayElement && nearestNextDayElement.start < end) {
        return false;
      }

      // prevent from overlapping with nearest production element from previous day
      if (
        start < startOfDay &&
        nearestPreviousDayProductionElement &&
        nearestPreviousDayProductionElement.end > start
      ) {
        return false;
      }

      // prevent from overlapping with nearest production element from next day
      if (end > endOfDay && nearestNextDayProductionElement && nearestNextDayProductionElement.start < end) {
        return false;
      }

      // Forbid elements overflow
      for (const point of allPoints) {
        // Allow dragging when two dragged elements are about to overflow
        if (draggedPointsKeys.includes(point.key)) {
          continue;
        }

        // Disallow dragging when hit non-dragged elements
        if (y === point.y && start < point.end && end > point.start) {
          return false;
        }
      }

      for (const productionElement of productionScheduleElements) {
        // Disallow dragging when hit production elements
        if (y === productionElement.y && start < productionElement.end && end > productionElement.start) {
          return false;
        }
      }
    }

    // Do not overflow dragged elements when reaching top/bottom of the graph
    if (uniq(yAxisDeltas).length > 1) {
      return false;
    }

    return true;
  };
